import React, { useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { toast } from "sonner"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { InfoTooltip } from "@/components/ui/info-tooltip"
import { ApiKeyField } from "@/components/domain/api-key-field"
import { useCan } from "@/lib/authz"
import type { InertiaFormObject, SharedProps } from "@/types/inertia"

type CompanyProps = {
  id: number
  key: string
  name: string
  baseUrl: string
  apiKey: string
  jwtSecret: string
  jwtSecretSet: boolean
  corsOrigins: string[]
  createdAt: string
  mfaRequired: boolean
  autoApproveEscalationCategories: boolean
  slaDefaultFirstResponseMinutes: number | null
  slaDefaultIdleResponseMinutes: number | null
  slaDefaultResolutionMinutes: number | null
  slaAttainmentGoalFirstResponsePct: number | null
  slaAttainmentGoalResolutionPct: number | null
  slaAttainmentGoalAvgFirstResponseSeconds: number | null
}

type ModelOption = {
  value: number
  label: string
  key: string
  provider: string | null
  family: string | null
  familyLatest: boolean
  actualVersionName: string | null
}

type ModelOptionGroup = {
  label: string
  options: ModelOption[]
}

type ModelSettingsProps = {
  options: ModelOptionGroup[]
  forgeModelVersionId: number | null
  agentModelVersionId: number | null
}

type ProfileProps = {
  firstName: string | null
  lastName: string | null
  email: string
  displayName: string
  avatarUrl: string | null
  mfaEnrolled: boolean
}

type Props = {
  tab: string
  company: CompanyProps
  profile: ProfileProps
  model_settings: ModelSettingsProps
  default_locale: string
  supported_locales: string[]
}

export default function SettingsIndex({ tab, company, profile, model_settings, default_locale, supported_locales }: Props) {
  const { props } = usePage<SharedProps>()
  const flash = props.flash

  return (
    <AppShell>
      <PageHeader
        eyebrow="Settings"
        title="Workspace settings"
        subtitle={`Tenant: ${company.name}`}
      />

      {flash.notice && (
        <div className="rounded-control border border-status-success/30 bg-status-success/10 px-4 py-2 text-sm text-status-success">
          {flash.notice}
        </div>
      )}
      {flash.alert && (
        <div className="rounded-control border border-status-error/30 bg-status-error/10 px-4 py-2 text-sm text-status-error">
          {flash.alert}
        </div>
      )}

      <section className="space-y-5">
        {tab === "company" && (
          <CompanyTab
            company={company}
            modelSettings={model_settings}
            defaultLocale={default_locale}
            supportedLocales={supported_locales}
          />
        )}
        {tab === "integration" && <IntegrationTab company={company} />}
        {tab === "security" && <SecurityTab profile={profile} />}
        {tab === "profile" && <ProfileTab profile={profile} />}
      </section>
    </AppShell>
  )
}

// Security tab — links to per-user MFA management. Company-wide policy
// toggles (require-mfa-for-all, session lifetime) come in a follow-up.
function SecurityTab({ profile }: { profile: ProfileProps }) {
  return (
    <Card className="p-5 sm:p-6">
      <header className="mb-5">
        <h2 className="text-lg font-semibold">Security</h2>
        <p className="text-sm text-app-muted">
          Two-factor authentication and session management.
        </p>
      </header>

      <div className="space-y-5">
        <Field label="Two-factor authentication" hint={profile.mfaEnrolled
          ? "Enrolled. Open the MFA page to manage passkeys, authenticator app, and recovery codes."
          : "Not enrolled. Set up a passkey or authenticator app to protect your account."}>
          <Button onClick={() => (window.location.href = "/portal/mfa/enroll")} className="w-full sm:w-auto">
            {profile.mfaEnrolled ? "Manage 2FA" : "Set up 2FA"}
          </Button>
        </Field>
      </div>
    </Card>
  )
}

// Profile tab — per-user identity (name, avatar) + 2FA shortcut.
function ProfileTab({ profile }: { profile: ProfileProps }) {
  const [firstName, setFirstName] = useState(profile.firstName ?? "")
  const [lastName, setLastName] = useState(profile.lastName ?? "")
  const [submitting, setSubmitting] = useState(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  function saveNames(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)
    router.patch("/settings", {
      section: "profile",
      profile: { first_name: firstName, last_name: lastName },
    }, {
      preserveScroll: true,
      onFinish: () => setSubmitting(false),
    })
  }

  function uploadAvatar(file: File) {
    const fd = new FormData()
    fd.append("avatar", file)
    router.post("/settings/avatar", fd, { preserveScroll: true, forceFormData: true })
  }

  function removeAvatar() {
    if (!confirm("Remove your avatar?")) return
    router.delete("/settings/avatar", { preserveScroll: true })
  }

  return (
    <Card className="p-5 sm:p-6">
      <header className="mb-5">
        <h2 className="text-lg font-semibold">Your profile</h2>
        <p className="text-sm text-app-muted">
          Display name, avatar, and second-factor management.
        </p>
      </header>

      <form onSubmit={saveNames} className="space-y-5">
        <Field label="Avatar" hint="Square image, 256×256 or larger. PNG / JPG / SVG.">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            {profile.avatarUrl ? (
              <img
                src={profile.avatarUrl}
                alt={profile.displayName}
                className="h-16 w-16 shrink-0 rounded-control border border-border object-cover"
              />
            ) : (
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-control bg-gradient-to-br from-brand-red to-brand-redDeep text-xl font-semibold text-white">
                {(profile.displayName.trim()[0] ?? "?").toUpperCase()}
              </div>
            )}
            <div className="flex flex-row gap-2 sm:flex-col">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) uploadAvatar(f)
                  e.target.value = ""
                }}
                className="hidden"
              />
              <Button type="button" variant="ghost" onClick={() => fileInputRef.current?.click()}>
                Upload new
              </Button>
              {profile.avatarUrl && (
                <Button type="button" variant="ghost" onClick={removeAvatar}>
                  Remove
                </Button>
              )}
            </div>
          </div>
        </Field>

        <Field label="First name">
          <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Adam" />
        </Field>
        <Field label="Last name">
          <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Smith" />
        </Field>
        <Field label="Email">
          <Input value={profile.email} disabled />
        </Field>

        <div>
          <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
            {submitting ? "Saving…" : "Save profile"}
          </Button>
        </div>
      </form>
    </Card>
  )
}

function CompanyTab({
  company,
  modelSettings,
  defaultLocale,
  supportedLocales,
}: {
  company: CompanyProps
  modelSettings: ModelSettingsProps
  defaultLocale: string
  supportedLocales: string[]
}) {
  const [name, setName] = useState(company.name)
  const [baseUrl, setBaseUrl] = useState(company.baseUrl)
  const [mfaRequired, setMfaRequired] = useState(company.mfaRequired)
  const [autoApproveCategories, setAutoApproveCategories] = useState(company.autoApproveEscalationCategories)
  const [localeKey, setLocaleKey] = useState<string>(defaultLocale)
  const [forgeModelId, setForgeModelId] = useState<number | null>(modelSettings.forgeModelVersionId)
  const [agentModelId, setAgentModelId] = useState<number | null>(modelSettings.agentModelVersionId)

  // Debounced PATCH to /settings/company/preferences for the
  // default-locale dropdown. Mirrors the 350ms pattern used by the
  // Onboarding-tab provider picker — single-key writes happen
  // out-of-band of the main "Save changes" form, so an operator
  // scrubbing through the dropdown via keyboard doesn't fire one
  // PATCH per option.
  const localePatchTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const modelPatchTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const modelOptions = modelSettings.options ?? []
  const flatModelOptions = modelOptions.flatMap((g) => g.options)
  const findModelOption = (id: number | null) => flatModelOptions.find((o) => o.value === id) ?? null
  const patchCompanyPreference = (payload: Record<string, unknown>, successMessage: string) => {
    const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? ""
    return fetch("/settings/company/preferences", {
      method:      "PATCH",
      credentials: "same-origin",
      headers:     { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf },
      body:        JSON.stringify(payload),
    })
      .then((res) => {
        if (res.ok) toast.success(successMessage)
        else toast.error("Could not update company setting")
      })
      .catch(() => toast.error("Could not update company setting"))
  }
  const onPickLocale = (next: string) => {
    if (!next || next === localeKey) return
    setLocaleKey(next)
    if (localePatchTimer.current) clearTimeout(localePatchTimer.current)
    localePatchTimer.current = setTimeout(() => {
      void patchCompanyPreference({ default_locale: next }, "Default locale updated")
    }, 350)
  }
  const onPickModel = (kind: "forge" | "agent", next: number | null) => {
    if (next == null) return
    if (kind === "forge") {
      if (next === forgeModelId) return
      setForgeModelId(next)
    } else {
      if (next === agentModelId) return
      setAgentModelId(next)
    }
    if (modelPatchTimer.current) clearTimeout(modelPatchTimer.current)
    modelPatchTimer.current = setTimeout(() => {
      const payload = kind === "forge"
        ? { forge_model_version_id: next }
        : { agent_model_version_id: next }
      void patchCompanyPreference(payload, kind === "forge" ? "Forge model updated" : "Agent model updated")
    }, 250)
  }
  // SLA defaults — empty string = "no default, clock disabled". Stored as
  // strings in state (not numbers) so the input can hold an empty value
  // while editing without snapping back to "0".
  const [slaFirstResponse, setSlaFirstResponse] = useState<string>(
    company.slaDefaultFirstResponseMinutes == null ? "" : String(company.slaDefaultFirstResponseMinutes),
  )
  const [slaIdleResponse, setSlaIdleResponse] = useState<string>(
    company.slaDefaultIdleResponseMinutes == null ? "" : String(company.slaDefaultIdleResponseMinutes),
  )
  const [slaResolution, setSlaResolution] = useState<string>(
    company.slaDefaultResolutionMinutes == null ? "" : String(company.slaDefaultResolutionMinutes),
  )
  // Attainment goals — dashboard-only target lines / KPI thresholds. Distinct
  // from the per-ticket SLA targets above. Empty string = "no goal, hide line".
  const [slaGoalFirstResponsePct, setSlaGoalFirstResponsePct] = useState<string>(
    company.slaAttainmentGoalFirstResponsePct == null ? "" : String(company.slaAttainmentGoalFirstResponsePct),
  )
  const [slaGoalResolutionPct, setSlaGoalResolutionPct] = useState<string>(
    company.slaAttainmentGoalResolutionPct == null ? "" : String(company.slaAttainmentGoalResolutionPct),
  )
  const [slaGoalAvgFirstResponseSeconds, setSlaGoalAvgFirstResponseSeconds] = useState<string>(
    company.slaAttainmentGoalAvgFirstResponseSeconds == null ? "" : String(company.slaAttainmentGoalAvgFirstResponseSeconds),
  )
  const [submitting, setSubmitting] = useState(false)
  const can = useCan()

  // Same convention as the policy editor: idle SLA without first-response
  // SLA leaves the initial wait window untracked. Yellow inline note, no
  // save-disable.
  const slaIdleWithoutFirstResponse =
    slaIdleResponse.trim() !== "" && slaFirstResponse.trim() === ""

  function save(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)
    const payload: InertiaFormObject = {
      name,
      base_url: baseUrl,
      auto_approve_escalation_categories: autoApproveCategories,
      sla_default_first_response_minutes: slaFirstResponse.trim() === "" ? "" : Number(slaFirstResponse),
      sla_default_idle_response_minutes:  slaIdleResponse.trim()  === "" ? "" : Number(slaIdleResponse),
      sla_default_resolution_minutes:     slaResolution.trim()    === "" ? "" : Number(slaResolution),
      sla_attainment_goal_first_response_pct:           slaGoalFirstResponsePct.trim()         === "" ? "" : Number(slaGoalFirstResponsePct),
      sla_attainment_goal_resolution_pct:               slaGoalResolutionPct.trim()            === "" ? "" : Number(slaGoalResolutionPct),
      sla_attainment_goal_avg_first_response_seconds:   slaGoalAvgFirstResponseSeconds.trim()  === "" ? "" : Number(slaGoalAvgFirstResponseSeconds),
    }
    if (can("company_mfa_policy")) payload.mfa_required = mfaRequired
    router.patch("/settings", {
      section: "company",
      company: payload,
    }, {
      preserveScroll: true,
      onFinish: () => setSubmitting(false),
    })
  }

  return (
    <Card className="p-5 sm:p-6">
      <header className="mb-5">
        <h2 className="text-lg font-semibold">Company information</h2>
        <p className="text-sm text-app-muted">Public-facing identity and the base URL we hit for partner callbacks.</p>
      </header>

      <form onSubmit={save} className="space-y-5">
        <Field label="Display name" hint="Shown in the portal header and in the company switcher.">
          <Input value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>

        <Field label="Slug" hint="Internal key — read-only. Used in URLs and integration callbacks.">
          <code className="block rounded-control border border-border bg-white/5 px-3 py-2 font-mono text-sm text-app-muted">
            {company.key}
          </code>
        </Field>

        <Field label="Base URL" hint="The partner's public app URL. Used to construct callback links and signed redirect targets.">
          <Input
            type="url"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://app.example.com"
            required
          />
        </Field>

        {can("company_mfa_policy") && (
          <Field label="Require 2FA for all members" hint="When on, every member is forced to enroll a passkey or authenticator app on next sign-in. Only super admins can change this.">
            <label className="inline-flex items-center gap-2 text-sm text-app-text">
              <input
                type="checkbox"
                checked={mfaRequired}
                onChange={(e) => setMfaRequired(e.target.checked)}
                className="h-4 w-4 accent-brand-red"
              />
              {mfaRequired ? "Required" : "Optional"}
            </label>
          </Field>
        )}

        <Field
          label={
            <span className="inline-flex items-center gap-1.5">
              Auto-approve new categories
              <InfoTooltip side="right">
                When the AI gets a new kind of question it has not seen before,
                it makes up a name for it (a "category") and adds it to your
                list. If this box is checked, the new name shows up right away
                so you can use it. If it is unchecked, the name waits in a pile
                for you to look at first — you can keep it, change it, or throw
                it away before anyone else sees it.
              </InfoTooltip>
            </span>
          }
          hint="On by default. Turn off if you'd rather review each new category before it counts."
        >
          <label className="inline-flex items-center gap-2 text-sm text-app-text">
            <input
              type="checkbox"
              checked={autoApproveCategories}
              onChange={(e) => setAutoApproveCategories(e.target.checked)}
              className="h-4 w-4 accent-brand-red"
            />
            {autoApproveCategories ? "Auto-approve on" : "Review every proposal"}
          </label>
        </Field>

        <Field
          label="Default locale"
          hint="BCP 47 tag used by Valet when no per-appuser locale is set."
        >
          <div className="max-w-xs">
            <Select<string>
              value={{ value: localeKey, label: localeKey }}
              options={supportedLocales.map((l) => ({ value: l, label: l }))}
              onChange={(o) => onPickLocale(o?.value ?? "")}
              placeholder="Choose a locale…"
            />
          </div>
        </Field>

        <div className="space-y-3 rounded-control border border-border bg-white/[0.02] p-4">
          <div>
            <div className="text-sm font-medium text-app-text">Model defaults</div>
            <div className="mt-1 text-xs text-app-muted">
              Agent defaults start on Haiku latest; Forge defaults start on Opus latest. Choose a family marker to track upgrades automatically, or pin a specific version for slower rollouts.
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <Field
              label="Forge model"
              hint="Used by Forge for skill design, policy design, draft creation, and red-team generation."
            >
              <Select<number>
                value={findModelOption(forgeModelId)}
                options={modelOptions}
                onChange={(o) => onPickModel("forge", o?.value ?? null)}
                placeholder="Choose a Forge model…"
                formatGroupLabel={(group) => (
                  <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-app-muted">
                    <span>{group.label}</span>
                    <span>{group.options.length}</span>
                  </div>
                )}
              />
            </Field>
            <Field
              label="Agent model"
              hint="Default runtime model for agents that inherit the company model. Per-agent overrides still win."
            >
              <Select<number>
                value={findModelOption(agentModelId)}
                options={modelOptions}
                onChange={(o) => onPickModel("agent", o?.value ?? null)}
                placeholder="Choose an agent model…"
                formatGroupLabel={(group) => (
                  <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-app-muted">
                    <span>{group.label}</span>
                    <span>{group.options.length}</span>
                  </div>
                )}
              />
            </Field>
          </div>
        </div>

        <div className="space-y-3 rounded-control border border-border bg-white/[0.02] p-4">
          <div>
            <div className="text-sm font-medium text-app-text">Default SLA targets</div>
            <div className="mt-1 text-xs text-app-muted">
              Used when no escalation policy matches a ticket, or when a matched policy leaves a field blank.
              Leave any field blank to disable that clock by default.
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Default first-response SLA (min)">
              <Input
                type="number"
                min={1}
                value={slaFirstResponse}
                onChange={(e) => setSlaFirstResponse(e.target.value)}
                placeholder="off"
              />
            </Field>
            <Field label="Default idle-response SLA (min)">
              <Input
                type="number"
                min={1}
                value={slaIdleResponse}
                onChange={(e) => setSlaIdleResponse(e.target.value)}
                placeholder="off"
              />
            </Field>
            <Field label="Default resolution SLA (min)">
              <Input
                type="number"
                min={1}
                value={slaResolution}
                onChange={(e) => setSlaResolution(e.target.value)}
                placeholder="off"
              />
            </Field>
          </div>
          {slaIdleWithoutFirstResponse && (
            <div className="rounded-control border border-status-warning/40 bg-status-warning/10 px-3 py-2 text-[11px] text-status-warning">
              Idle SLA without a first-response SLA means the initial wait is untracked.
            </div>
          )}
        </div>

        <div className="space-y-3 rounded-control border border-border bg-white/[0.02] p-4">
          <div>
            <div className="text-sm font-medium text-app-text">Attainment goals</div>
            <div className="mt-1 text-xs text-app-muted">
              Target percentages and averages used to draw goal lines on the SLA Performance dashboard.
              Leave blank to hide.
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="First-response attainment goal (%)">
              <Input
                type="number"
                min={1}
                max={100}
                value={slaGoalFirstResponsePct}
                onChange={(e) => setSlaGoalFirstResponsePct(e.target.value)}
                placeholder="e.g. 90"
              />
            </Field>
            <Field label="Resolution attainment goal (%)">
              <Input
                type="number"
                min={1}
                max={100}
                value={slaGoalResolutionPct}
                onChange={(e) => setSlaGoalResolutionPct(e.target.value)}
                placeholder="e.g. 90"
              />
            </Field>
            <Field label="Avg. first response goal (seconds)">
              <Input
                type="number"
                min={1}
                value={slaGoalAvgFirstResponseSeconds}
                onChange={(e) => setSlaGoalAvgFirstResponseSeconds(e.target.value)}
                placeholder="e.g. 300"
              />
            </Field>
          </div>
        </div>

        <div className="flex justify-stretch gap-3 sm:justify-end">
          <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
            {submitting ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>
    </Card>
  )
}

function IntegrationTab({ company }: { company: CompanyProps }) {
  return (
    <div className="space-y-6">
      <Card className="p-5 sm:p-6">
        <header className="mb-5">
          <h2 className="text-lg font-semibold">API key</h2>
          <p className="text-sm text-app-muted">
            Send this in the <code className="rounded bg-white/5 px-1 py-0.5 text-xs">X-Company-Api-Key</code> header
            on requests to <code className="rounded bg-white/5 px-1 py-0.5 text-xs">/api/v1/*</code>. Keep it secret.
            Rotating invalidates the previous value immediately.
          </p>
        </header>

        <ApiKeyField value={company.apiKey} rotateUrl="/settings/rotate_api_key" />
      </Card>

      <Card className="p-5 sm:p-6">
        <header className="mb-5">
          <h2 className="text-lg font-semibold">JWT signing secret <span className="text-xs font-normal text-app-muted">(v2 SDK)</span></h2>
          <p className="text-sm text-app-muted">
            Used by your backend to sign 15-min HS256 tokens for the
            browser <code className="rounded bg-white/5 px-1 py-0.5 text-xs">@valet/sdk</code>. Tokens carry
            <code className="rounded bg-white/5 px-1 py-0.5 text-xs">aud="valet-sdk"</code> + your
            <code className="rounded bg-white/5 px-1 py-0.5 text-xs">company_key</code> as <code className="rounded bg-white/5 px-1 py-0.5 text-xs">iss</code>.
            Keep this secret on your server — never embed it in browser code.
          </p>
          <p className="mt-2 text-sm text-app-muted">
            <strong>Rotation</strong> stashes the current secret as
            <code className="rounded bg-white/5 px-1 py-0.5 text-xs">previous</code>; tokens already minted
            keep verifying for one rotation window (≤15 min) before naturally expiring. Then they fail and
            new tokens must use the new secret.
          </p>
        </header>

        {company.jwtSecretSet ? (
          <ApiKeyField value={company.jwtSecret} rotateUrl="/settings/rotate_jwt_secret" />
        ) : (
          <NotSetField rotateUrl="/settings/rotate_jwt_secret" />
        )}
      </Card>

      <CorsOriginsCard initial={company.corsOrigins} />
    </div>
  )
}

function CorsOriginsCard({ initial }: { initial: string[] }) {
  const [origins, setOrigins] = useState<string[]>(initial)
  const [draft, setDraft]     = useState("")
  const [saving, setSaving]   = useState(false)
  const [dirty, setDirty]     = useState(false)

  function commitDraft() {
    // Accept comma- or newline-separated paste in the same input.
    const parts = draft.split(/[\s,]+/).map((s) => s.trim()).filter((s) => s.length > 0)
    if (parts.length === 0) return
    setOrigins((prev) => Array.from(new Set([...prev, ...parts])))
    setDraft("")
    setDirty(true)
  }

  function removeAt(idx: number) {
    setOrigins((prev) => prev.filter((_, i) => i !== idx))
    setDirty(true)
  }

  function save() {
    setSaving(true)
    router.patch("/settings", { section: "cors", cors_origins: origins }, {
      preserveScroll: true,
      onSuccess:      () => setDirty(false),
      onFinish:       () => setSaving(false)
    })
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault()
      // If the input has content, commit it as a token. If it's empty
      // and we have unsaved changes, save the form. Both feel right.
      if (draft.trim().length > 0) commitDraft()
      else if (dirty) save()
    } else if (e.key === "Backspace" && draft.length === 0 && origins.length > 0) {
      // Backspace on empty input pops the last token — same UX as
      // GitHub topic editor / Slack channel picker.
      removeAt(origins.length - 1)
    }
  }

  return (
    <Card className="p-5 sm:p-6">
      <header className="mb-5">
        <h2 className="text-lg font-semibold">CORS allowed origins <span className="text-xs font-normal text-app-muted">(v2 SDK)</span></h2>
        <p className="text-sm text-app-muted">
          Browser SDKs can only open the SSE stream from origins you list here.
          Format <code className="rounded bg-white/5 px-1 py-0.5 text-xs">scheme://host[:port]</code> — no trailing slashes, no paths.
          Press <kbd className="rounded bg-white/10 px-1 text-xs">Enter</kbd> to add. Empty list blocks all browser integrations.
        </p>
        <p className="mt-2 text-xs text-app-muted">
          Examples: <code className="rounded bg-white/5 px-1 py-0.5">https://yourapp.com</code>,
          <code className="rounded bg-white/5 px-1 py-0.5 ml-1">http://localhost:3000</code>
        </p>
      </header>

      <div className="rounded-control border border-border bg-white/5 px-3 py-2 focus-within:border-app-accent">
        <div className="flex flex-wrap items-center gap-2">
          {origins.map((o, idx) => (
            <span
              key={`${o}-${idx}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 font-mono text-xs text-app-text"
            >
              {o}
              <button
                type="button"
                onClick={() => removeAt(idx)}
                aria-label={`Remove ${o}`}
                className="flex h-4 w-4 items-center justify-center rounded-full text-app-muted hover:bg-white/20 hover:text-app-text"
              >
                ×
              </button>
            </span>
          ))}
          <input
            type="text"
            value={draft}
            placeholder={origins.length === 0 ? "https://yourapp.com" : "add another…"}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={() => { if (draft.trim().length > 0) commitDraft() }}
            className="flex-1 min-w-[16ch] bg-transparent py-1 font-mono text-sm text-app-text outline-none"
          />
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Button variant="gold" size="sm" type="button" onClick={save} disabled={saving || !dirty}>
          {saving ? "Saving…" : (dirty ? "Save" : "Saved")}
        </Button>
        {dirty && (
          <span className="text-xs text-app-muted">Unsaved changes</span>
        )}
      </div>
    </Card>
  )
}

function NotSetField({ rotateUrl }: { rotateUrl: string }) {
  function generate() {
    if (!confirm("Generate a JWT secret? Copy the value once it appears — share it with your backend over a secure channel (1Password, encrypted message).")) return
    router.post(rotateUrl)
  }
  return (
    <div className="space-y-3">
      <p className="text-sm text-app-muted">No JWT secret set yet.</p>
      <Button variant="gold" size="sm" type="button" onClick={generate}>
        Generate JWT secret
      </Button>
    </div>
  )
}

function Field({ label, hint, children }: { label: React.ReactNode; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-sm font-medium text-app-text">{label}</div>
      {hint && <div className="mb-2 text-xs text-app-muted">{hint}</div>}
      {children}
    </label>
  )
}
